import type {
  CalendarDate,
  CalendarDay,
  CalendarDayState,
  CalendarInvalidReason,
  CalendarSelection,
} from './Calendar.types'
import { calendarDaysBetween } from './calendarDate'

export interface CalendarRangeRules {
  minRangeLength?: number
  maxRangeLength?: number
  /**
   * PROVISIONAL. Nights is the default because hotel booking dominates here:
   * a Friday-to-Sunday stay is two nights, and the check-out day has no
   * price because nobody sleeps on it. Calendars counting whole days —
   * car hire, experiences — pass `'day'`.
   */
  rangeUnit?: 'night' | 'day'
  allowSameDayRange?: boolean
}

export interface CalendarSelectionResult {
  selection: CalendarSelection
  invalidReason?: CalendarInvalidReason
}

export function emptySelection(mode: CalendarSelection['mode']): CalendarSelection {
  switch (mode) {
    case 'single': return { mode: 'single', date: undefined }
    case 'range': return { mode: 'range', range: undefined }
    case 'multiple': return { mode: 'multiple', dates: new Set() }
  }
}

/** Length of a range in the configured unit. */
export function getRangeLength(
  from: CalendarDate,
  to: CalendarDate,
  rangeUnit: 'night' | 'day' = 'night',
): number {
  const nights = calendarDaysBetween(from, to)
  return rangeUnit === 'night' ? nights : nights + 1
}

function checkRangeLength(
  from: CalendarDate,
  to: CalendarDate,
  rules: CalendarRangeRules,
): CalendarInvalidReason | undefined {
  const { minRangeLength, maxRangeLength, rangeUnit = 'night' } = rules
  const length = getRangeLength(from, to, rangeUnit)

  if (minRangeLength !== undefined && length < minRangeLength) return 'below-min-length'
  if (maxRangeLength !== undefined && length > maxRangeLength) return 'above-max-length'
  return undefined
}

/**
 * The next selection after a day is clicked. Pure — the caller decides what
 * to do with an invalid result, because only it knows what to say about it.
 */
export function selectDay(
  current: CalendarSelection,
  date: CalendarDate,
  rules: CalendarRangeRules = {},
): CalendarSelectionResult {
  switch (current.mode) {
    case 'single':
      return { selection: { mode: 'single', date } }

    case 'multiple': {
      const dates = new Set(current.dates)
      if (dates.has(date)) {
        dates.delete(date)
      } else {
        dates.add(date)
      }
      return { selection: { mode: 'multiple', dates } }
    }

    case 'range': {
      const range = current.range

      const startFresh: CalendarSelectionResult = {
        selection: { mode: 'range', range: { from: date, to: undefined } },
      }

      if (!range || range.to !== undefined) return startFresh
      if (date < range.from) return startFresh

      if (date === range.from) {
        if (!rules.allowSameDayRange) return startFresh
        const invalidReason = checkRangeLength(range.from, date, rules)
        if (invalidReason) return { selection: current, invalidReason }
        return { selection: { mode: 'range', range: { from: range.from, to: date } } }
      }

      const invalidReason = checkRangeLength(range.from, date, rules)
      if (invalidReason) return { selection: current, invalidReason }

      return { selection: { mode: 'range', range: { from: range.from, to: date } } }
    }
  }
}

export interface ResolveDayStateOptions {
  selection?: CalendarSelection
  /** Date currently hovered, when previewing a candidate range end. */
  previewDate?: CalendarDate
  isDisabled?: boolean
}

export function resolveDayState(day: CalendarDay, options: ResolveDayStateOptions = {}): CalendarDayState {
  const { selection, previewDate, isDisabled } = options

  if (isDisabled || day.isOutOfBounds || day.isAdjacentMonth) return 'disabled'
  if (!selection) return 'idle'

  switch (selection.mode) {
    case 'single':
      return selection.date === day.date ? 'selected' : 'idle'

    case 'multiple':
      return selection.dates.has(day.date) ? 'selected' : 'idle'

    case 'range': {
      const range = selection.range
      if (!range) return 'idle'

      if (day.date === range.from) return 'range-start'
      if (range.to !== undefined) {
        if (day.date === range.to) return 'range-end'
        return day.date > range.from && day.date < range.to ? 'in-range' : 'idle'
      }

      if (previewDate !== undefined && previewDate > range.from) {
        if (day.date === previewDate) return 'preview-end'
        if (day.date > range.from && day.date < previewDate) return 'preview-range'
      }

      return 'idle'
    }
  }
}
